import { useState, useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './AuthContext'
import { AppErrorBoundary, FeatureErrorBoundary } from './ErrorBoundary'
import AuthPage from './AuthPage'
import LandingPage from './LandingPage'
import PrivacyPolicy from './PrivacyPolicy'
import TermsOfService from './TermsOfService'
import OfflineBanner from './components/OfflineBanner'
import UpdatePrompt from './components/UpdatePrompt'
import { supabase } from './supabaseClient'
import { useProfile } from './hooks/useProfile'

// Lazy-load heavy feature components
const Dashboard = lazy(() => import('./Dashboard'))
const HRDashboard = lazy(() => import('./HRDashboard'))
const SharedPlanViewer = lazy(() => import('./SharedPlanViewer'))
const ProfileOnboarding = lazy(() => import('./onboarding/ProfileOnboarding'))
const ModeSelector = lazy(() => import('./onboarding/ModeSelector'))
const CommunityShell = lazy(() => import('./community/CommunityShell'))

function LoadingFallback() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#FAFAF7', fontFamily: "'Outfit', sans-serif",
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12, background: '#0F6E56',
          margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}>
          <svg width="24" height="24" viewBox="0 0 64 64" fill="none">
            <circle cx="32" cy="13" r="5.5" fill="#BA7517"/>
            <path d="M32 18.5C32 18.5 38 25.5 38 31c0 3.8-2.2 6-6 7.8s-7.5 4-7.5 7.7c0 2.2 1.3 3.5 4.5 3.5" stroke="#fff" strokeWidth="4" strokeLinecap="round" fill="none"/>
            <circle cx="31" cy="50.5" r="3.5" fill="#BA7517"/>
          </svg>
        </div>
        <div style={{ color: '#8C8C8C', fontSize: 15 }}>Loading Settle...</div>
        <style>{`@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }`}</style>
      </div>
    </div>
  )
}

// ── Invite code redemption handler ──────────────────────────────────────
// Calls the redeem_invite_code RPC, which performs validation + redemption
// atomically (SELECT ... FOR UPDATE inside a SECURITY DEFINER function).
// See supabase/migrations/20260417_atomic_invite_redemption.sql.
function useInviteRedemption(user) {
  useEffect(() => {
    if (!user) return
    let cancelled = false

    async function redeemInvite() {
      const path = window.location.pathname
      const match = path.match(/^\/join\/(.+)$/)
      if (!match) return

      const code = match[1]

      const { error } = await supabase.rpc('redeem_invite_code', { p_code: code })

      if (cancelled) return

      if (error) {
        // P0002 = already used, P0003 = expired, P0001 = not found.
        // Log but don't block navigation; the user is authenticated and can
        // still use the app without the invite.
        console.warn('[invite] redemption failed:', error.message)
      }

      // Clear the URL regardless of success so the code isn't re-attempted on reload.
      window.history.replaceState({}, '', '/')
    }

    redeemInvite()
    return () => { cancelled = true }
  }, [user])
}

function AppContent() {
  const { user } = useAuth()
  const location = useLocation()
  const [page, setPage] = useState('landing') // landing | auth | privacy | terms
  const [accountType, setAccountType] = useState('individual') // individual | hr
  const [isHRAdmin, setIsHRAdmin] = useState(null) // null = loading, true/false after check
  const [forceHR, setForceHR] = useState(false) // true when user clicks "HR" on landing

  // Post-signup profile gate — shown to every logged-in user before routing
  // them to their dashboard. The row is captured in ProfileOnboarding and
  // persists forever; once set, this gate becomes a no-op.
  //
  // Note: useProfile returns profile===null both when the user has no row
  // AND while the initial fetch is in flight. We distinguish via `loading`.
  const {
    profile,
    loading: profileLoading,
    error: profileError,
    refresh: refreshProfile,
    updateProfile,
  } = useProfile()

  // Mode switch handler — updates preferred_mode in user_profiles and
  // relies on useProfile's internal setState to re-render with the new
  // mode. Kept here so both Dashboard and CommunityShell share one impl.
  const handleSwitchMode = async (nextMode) => {
    if (nextMode !== 'plan' && nextMode !== 'community') return
    if (profile?.preferred_mode === nextMode) return
    await updateProfile({ preferred_mode: nextMode })
  }

  // Handle invite code redemption
  useInviteRedemption(user)

  // Check if user has HR admin role once logged in
  useEffect(() => {
    if (!user) { setIsHRAdmin(null); return }

    async function checkHR() {
      try {
        const { data, error } = await supabase
          .from('org_members')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .limit(1)

        // If table doesn't exist or query errors, treat as not HR admin
        if (error) { setIsHRAdmin(false); return }
        setIsHRAdmin(Array.isArray(data) && data.length > 0)
      } catch {
        setIsHRAdmin(false)
      }
    }
    checkHR()
  }, [user])

  // Handle "Get Started" with optional type
  const handleGetStarted = (type) => {
    if (type === 'hr') {
      setAccountType('hr')
      setForceHR(true)
    } else {
      setAccountType('individual')
      setForceHR(false)
    }
    setPage('auth')
  }

  // Legal pages by URL — /privacy and /terms must resolve for anyone, signed in
  // or not, because the App Store listing links straight to them.
  if (location.pathname === '/privacy') {
    return <PrivacyPolicy onBack={() => { window.history.pushState({}, '', '/'); setPage('landing') }} />
  }
  if (location.pathname === '/terms') {
    return <TermsOfService onBack={() => { window.history.pushState({}, '', '/'); setPage('landing') }} />
  }

  // Public shared plan viewer — accessible with or without login
  if (location.pathname.startsWith('/share/')) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <SharedPlanViewer />
      </Suspense>
    )
  }

  // Logged in → route to appropriate dashboard
  if (user) {
    // Profile gate: show onboarding if user has no profile row yet.
    // profileError is treated as "fail-open" — we don't want to block a user
    // from their data if the profile query has a transient failure.
    if (profileLoading) return <LoadingFallback />
    if (!profile && !profileError) {
      return (
        <Suspense fallback={<LoadingFallback />}>
          <FeatureErrorBoundary name="Profile Onboarding">
            <ProfileOnboarding onComplete={refreshProfile} />
          </FeatureErrorBoundary>
        </Suspense>
      )
    }

    // If user explicitly chose HR path, skip the DB check and go straight to HR
    if (forceHR) {
      return (
        <Suspense fallback={<LoadingFallback />}>
          <FeatureErrorBoundary name="HR Dashboard">
            <HRDashboard />
          </FeatureErrorBoundary>
        </Suspense>
      )
    }

    // Still checking HR status from DB
    if (isHRAdmin === null) return <LoadingFallback />

    // Returning HR admin → HR Dashboard (HR is its own separate surface;
    // the Plan/Community mode chooser does not apply to HR admins).
    if (isHRAdmin) {
      return (
        <Suspense fallback={<LoadingFallback />}>
          <FeatureErrorBoundary name="HR Dashboard">
            <HRDashboard />
          </FeatureErrorBoundary>
        </Suspense>
      )
    }

    // Mode gate — individual users who haven't yet picked a starting surface
    // see the ModeSelector. The choice writes preferred_mode to the profile,
    // and from then on this block short-circuits straight to the right shell.
    // Users can switch modes anytime from within either shell.
    if (!profile?.preferred_mode) {
      return (
        <Suspense fallback={<LoadingFallback />}>
          <FeatureErrorBoundary name="Mode Selector">
            <ModeSelector onComplete={refreshProfile} />
          </FeatureErrorBoundary>
        </Suspense>
      )
    }

    // Community mode
    if (profile.preferred_mode === 'community') {
      return (
        <Suspense fallback={<LoadingFallback />}>
          <FeatureErrorBoundary name="Community">
            <CommunityShell onSwitchMode={handleSwitchMode} />
          </FeatureErrorBoundary>
        </Suspense>
      )
    }

    // Plan mode (default)
    return (
      <Suspense fallback={<LoadingFallback />}>
        <FeatureErrorBoundary name="Dashboard">
          <Dashboard onSwitchMode={handleSwitchMode} />
        </FeatureErrorBoundary>
      </Suspense>
    )
  }

  // Handle /join/:code URL — show auth page so user can sign up/in, then code gets redeemed
  if (location.pathname.startsWith('/join/')) {
    return <AuthPage onBack={() => setPage('landing')} onNavigate={setPage} inviteCode={location.pathname.split('/join/')[1]} />
  }

  // Legal pages (accessible without login)
  if (page === 'privacy') return <PrivacyPolicy onBack={() => setPage('landing')} />
  if (page === 'terms') return <TermsOfService onBack={() => setPage('landing')} />

  // Auth page
  if (page === 'auth') return <AuthPage onBack={() => setPage('landing')} onNavigate={setPage} accountType={accountType} />

  // Default → landing page
  return <LandingPage onGetStarted={handleGetStarted} onNavigate={setPage} />
}

export default function App() {
  return (
    <AppErrorBoundary>
      <OfflineBanner />
      <UpdatePrompt />
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  )
}
